import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, HeartHandshake, GraduationCap, Scale, Trees } from "lucide-react";
import api from "@/lib/api";
import EventCard from "@/components/EventCard";
import ProjectCard from "@/components/ProjectCard";
import { AREAS, formatDate } from "@/lib/constants";

const HERO_IMG = "https://images.pexels.com/photos/7281964/pexels-photo-7281964.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WORKSHOP_IMG = "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxwZW9wbGUlMjB3b3Jrc2hvcCUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzg0MTU0Mjk3fDA&ixlib=rb-4.1.0&q=85&w=1000";

const AREA_ICONS = {
  famiglie: HeartHandshake,
  bambini: Sparkles,
  scuola: GraduationCap,
  relazioni: Scale,
  comunita: Trees,
};

const WHY_VV = [
  { title: "Non devi avere già tutte le risposte", desc: "Crediamo che essere genitori non significhi dover sapere sempre cosa fare. Creiamo spazi in cui portare domande, esperienze e fatiche senza sentirsi giudicati." },
  { title: "Non sei solo/a", desc: "Favoriamo l'incontro tra famiglie e la nascita di reti reali. Perché una comunità può offrire ascolto, vicinanza, confronto e nuove possibilità." },
  { title: "Educhiamo attraverso le relazioni", desc: "Promuoviamo ascolto, empatia, rispetto dei bisogni e gestione nonviolenta dei conflitti, nelle famiglie e nei contesti educativi." },
  { title: "Cresciamo insieme al territorio", desc: "Costruiamo progetti insieme a scuole, associazioni, servizi, istituzioni e altre realtà, mettendo in relazione persone, esperienze e competenze." },
];

export default function Home() {
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [articles, setArticles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/events/upcoming?limit=3").then((r) => setEvents(r.data)),
      api.get("/projects?featured=true").then((r) => setProjects(r.data.slice(0, 6))),
      api.get("/articles/latest?limit=3").then((r) => setArticles(r.data)),
      api.get("/partners").then((r) => setPartners(r.data)),
      api.get("/stats").then((r) => setStats(r.data)),
    ]).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FFFCF8]">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] blob bg-[#FF6100]/12 -z-0" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] blob-2 bg-[#216B65]/10 -z-0" />
        <div className="container-vv relative py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative z-10">
            <div className="eyebrow">Vola Vola APS · Castel Maggiore (BO)</div>
            <h1 className="mt-4 font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#292522]">
              Famiglie,
              <br />
              relazioni,
              <br />
              <span className="text-[#FF6100]">comunità.</span>
            </h1>
            <p className="mt-6 text-lg text-[#292522]/75 max-w-xl leading-relaxed">
              Creiamo occasioni di incontro, partecipazione e crescita per famiglie, bambine, bambini e comunità. Ascolto, accoglienza e cura delle relazioni sono la nostra bussola.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/chi-siamo" className="btn-primary" data-testid="hero-cta-chi-siamo">
                Scopri Vola Vola <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
              <Link to="/eventi" className="btn-ghost" data-testid="hero-cta-eventi">
                Prossimi eventi
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-24 h-24 blob bg-[#FF6100]" />
              <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_20px_60px_rgba(41,37,34,0.15)]">
                <img src={HERO_IMG} alt="Famiglia insieme che condivide un momento" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-2xl shadow-lg p-4 max-w-[220px]">
                <div className="flex -space-x-2 mb-2">
                  <div className="h-8 w-8 rounded-full bg-[#FF6100]" />
                  <div className="h-8 w-8 rounded-full bg-[#216B65]" />
                  <div className="h-8 w-8 rounded-full bg-[#FFF1E8] border border-[#292522]/10" />
                </div>
                <p className="text-xs font-semibold text-[#292522]">Una comunità che si prende cura</p>
                <p className="text-[11px] text-[#292522]/60 mt-1">Persone, non utenti.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROSSIMAMENTE */}
      <section className="container-vv py-20 lg:py-28">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow">Prossimamente</div>
            <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522]">Vieni a un evento</h2>
          </div>
          <Link to="/eventi" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6100]" data-testid="link-tutti-eventi">
            Tutti gli eventi <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
        </div>
        {events.length === 0 ? (
          <div className="rounded-3xl bg-[#FFF1E8] p-10 text-center text-[#292522]/70">
            Nessun evento in programma al momento. Torna presto!
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((ev, i) => (
              <EventCard key={ev.id} event={ev} testId={`home-event-${i}`} />
            ))}
          </div>
        )}
      </section>

      {/* TRE FILI CHE TENGONO INSIEME VOLA VOLA */}
      <section className="bg-[#F8F1E8] py-20 lg:py-28">
        <div className="container-vv">
          <div className="max-w-3xl">
            <div className="eyebrow">Il nostro approccio</div>
            <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522]">Tre fili che tengono insieme Vola Vola.</h2>
            <p className="mt-5 text-lg text-[#292522]/75 leading-relaxed">
              Vola Vola fa molte cose diverse, ma dietro ai nostri progetti ci sono tre fili che ritornano sempre: costruire relazioni e reti tra pari, lavorare per la parità e contro la violenza di genere, diffondere un modo nonviolento di stare nelle relazioni e di educare. Cambiano i format; il punto di partenza resta lo stesso.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { key: "comunita", n: "01", title: "Comunità e reti tra pari", desc: "Creiamo spazi in cui incontrarsi, condividere esperienze e diventare risorsa le une per le altre." },
              { key: "parita", n: "02", title: "Parità e libertà dalla violenza", desc: "Lavoriamo sui rapporti di potere, sui ruoli di genere, sulla condivisione della cura e sulla prevenzione della violenza." },
              { key: "cnv", n: "03", title: "CNV e relazioni educative", desc: "Promuoviamo ascolto, empatia e strumenti nonviolenti nelle relazioni e nei contesti educativi." },
            ].map((f) => (
              <Link
                key={f.key}
                to={`/il-nostro-approccio#${f.key}`}
                className="card-vv bg-white p-8 group hover:-translate-y-1 transition-transform"
                data-testid={`filo-${f.key}`}
              >
                <div className="text-5xl font-display font-medium text-[#FF6100]/25">{f.n}</div>
                <h3 className="mt-4 font-display text-2xl font-medium text-[#292522] group-hover:text-[#FF6100] transition-colors">{f.title}</h3>
                <p className="mt-3 text-[#292522]/70 leading-relaxed">{f.desc}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6100]">
                  Approfondisci <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/il-nostro-approccio" className="btn-primary">Il nostro approccio <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
          </div>
        </div>
      </section>

      {/* PERCHÉ VOLA VOLA — Blocco distintivo */}
      <section className="container-vv py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="eyebrow">Perché Vola Vola</div>
          <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522] leading-tight">Non soltanto attività. Una comunità in cui crescere insieme.</h2>
          <p className="mt-6 text-lg text-[#292522]/80 leading-relaxed">
            Vola Vola non propone semplicemente incontri, laboratori ed eventi. Crea occasioni in cui famiglie, bambine, bambini e persone del territorio possono incontrarsi, sentirsi accolte e costruire relazioni autentiche. Crediamo in una genitorialità condivisa, in un'educazione fondata sull'ascolto e in una comunità capace di diventare una risorsa concreta nella vita quotidiana.
          </p>
        </div>
        <div className="mt-10">
          <div className="eyebrow text-[#216B65]">Perché scegliere di seguirci?</div>
        </div>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {WHY_VV.map((w, i) => (
            <div key={i} className="rounded-3xl bg-[#FFF1E8] p-8 relative" data-testid={`why-vv-${i}`}>
              <div className="absolute top-6 right-6 text-4xl font-display font-medium text-[#FF6100]/25">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="pr-10 font-display text-xl sm:text-2xl font-medium uppercase tracking-tight text-[#FF6100] leading-tight">{w.title}</h3>
              <p className="mt-3 text-[#292522]/80 leading-relaxed">{w.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 rounded-[2rem] bg-[#292522] text-[#FFFCF8] p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 blob bg-[#FF6100]/30 pointer-events-none" />
          <p className="relative text-xl sm:text-2xl font-display italic leading-snug max-w-3xl">
            Seguire Vola Vola significa trovare iniziative, strumenti e occasioni di incontro, ma soprattutto entrare in una comunità che considera la <span className="text-[#FF6100] font-medium">cura, l'educazione e le relazioni</span> una responsabilità condivisa.
          </p>
          <div className="relative mt-8 flex flex-wrap gap-3">
            <Link to="/eventi" className="btn-primary">Scopri i prossimi appuntamenti <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
            <Link to="/entra-nella-comunita" className="btn-ghost !bg-transparent !text-[#FFFCF8] !border-[#FFFCF8]/30 hover:!text-[#FF6100] hover:!border-[#FF6100]">Entra nella comunità</Link>
          </div>
        </div>
      </section>

      {/* PROGETTI IN EVIDENZA */}
      <section className="container-vv py-20 lg:py-28">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow">Progetti</div>
            <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522]">Dall'intreccio nascono i nostri progetti</h2>
          </div>
          <Link to="/progetti" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6100]">
            Tutti i progetti <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.slice(0, 6).map((p, i) => (
            <ProjectCard key={p.id} project={p} testId={`home-project-${i}`} />
          ))}
        </div>
      </section>

      {/* VOLA VOLA È UNA COMUNITÀ */}
      <section className="relative py-20 lg:py-28 bg-[#FFF1E8]">
        <div className="container-vv grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_20px_60px_rgba(41,37,34,0.12)]">
              <img src={WORKSHOP_IMG} alt="Persone che collaborano attorno a un tavolo" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow">Come lavoriamo</div>
            <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522] leading-tight">
              Vola Vola è una <span className="text-[#FF6100]">comunità.</span>
            </h2>
            <p className="mt-6 text-lg text-[#292522]/80 leading-relaxed max-w-2xl">
              Ascolto, accoglienza, non giudizio, partecipazione e cura delle relazioni sono alla base del nostro modo di lavorare. Crediamo che educazione, benessere e cura possano diventare più ricchi e più leggeri quando vengono condivisi.
            </p>
            <Link to="/chi-siamo#come" className="mt-8 btn-primary inline-flex" data-testid="cta-come-lavoriamo">
              Scopri come lavoriamo <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      {/* 5x1000 banner */}
      <section className="container-vv py-8">
        <Link
          to="/partecipa#cinquepermille"
          className="group block rounded-[2rem] bg-[#FFF1E8] p-6 sm:p-8 relative overflow-hidden hover:-translate-y-0.5 transition-transform"
          data-testid="banner-5x1000"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 blob bg-[#FF6100]/15 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
            <div className="flex items-center gap-6">
              <div className="text-6xl sm:text-7xl leading-none font-display font-medium text-[#FF6100]">5</div>
              <div>
                <div className="eyebrow">5 × 1000</div>
                <h3 className="mt-1 font-display text-2xl sm:text-3xl font-medium text-[#292522]">Sostienici senza spendere nulla</h3>
                <p className="mt-1 text-sm text-[#292522]/70">Firma per Vola Vola APS · C.F. <span className="font-mono font-semibold text-[#292522]">91436320377</span></p>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#FF6100] group-hover:gap-2.5 transition-all">
              Scopri come <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </span>
          </div>
        </Link>
      </section>

      {/* ELEMENTI DI FIDUCIA — real CMS numbers only */}
      {stats && (stats.projects_total > 0 || stats.events_total > 0 || stats.partners > 0) && (
        <section className="bg-[#F8F1E8] py-16 lg:py-20">
          <div className="container-vv">
            <div className="max-w-2xl">
              <div className="eyebrow">Prove concrete</div>
              <h2 className="mt-3 font-display font-medium text-3xl sm:text-4xl text-[#292522]">Cosa abbiamo fatto finora</h2>
              <p className="mt-3 text-[#292522]/70">Solo numeri reali dal nostro CMS, aggiornati automaticamente. Niente stime inventate.</p>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {stats.projects_total > 0 && (
                <div className="rounded-3xl bg-white p-8" data-testid="stat-projects">
                  <div className="text-5xl font-display font-medium text-[#FF6100]">{stats.projects_total}</div>
                  <div className="mt-2 text-sm text-[#292522]/70">Progetti nel nostro archivio</div>
                </div>
              )}
              {stats.events_total > 0 && (
                <div className="rounded-3xl bg-white p-8" data-testid="stat-events">
                  <div className="text-5xl font-display font-medium text-[#FF6100]">{stats.events_total}</div>
                  <div className="mt-2 text-sm text-[#292522]/70">Eventi pubblicati</div>
                </div>
              )}
              {stats.partners > 0 && (
                <div className="rounded-3xl bg-white p-8" data-testid="stat-partners">
                  <div className="text-5xl font-display font-medium text-[#FF6100]">{stats.partners}</div>
                  <div className="mt-2 text-sm text-[#292522]/70">Realtà con cui collaboriamo</div>
                </div>
              )}
              {stats.articles > 0 && (
                <div className="rounded-3xl bg-white p-8" data-testid="stat-articles">
                  <div className="text-5xl font-display font-medium text-[#FF6100]">{stats.articles}</div>
                  <div className="mt-2 text-sm text-[#292522]/70">Storie e risorse pubblicate</div>
                </div>
              )}
            </div>
            <p className="mt-6 text-xs text-[#292522]/50 italic">
              Vuoi aggiungere anni di attività dell'associazione o territori coinvolti? Basta creare i relativi campi dal pannello admin, appariranno qui automaticamente.
            </p>
          </div>
        </section>
      )}

      {/* ULTIME DA VOLA VOLA */}
      <section className="container-vv py-20 lg:py-28">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow">Storie e risorse</div>
            <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522]">Ultime da Vola Vola</h2>
          </div>
          <Link to="/storie" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF6100]">
            Tutte le storie <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {articles.map((a, i) => (
            <article key={a.id} className="card-vv p-8 bg-white flex flex-col" data-testid={`home-article-${i}`}>
              <span className="inline-block self-start rounded-full bg-[#FFF1E8] text-[#FF6100] text-xs font-semibold px-3 py-1">{a.category}</span>
              <h3 className="mt-4 font-display text-2xl font-medium text-[#292522]">{a.title}</h3>
              {a.subtitle && <p className="mt-2 text-sm text-[#292522]/65">{a.subtitle}</p>}
              <p className="mt-auto pt-6 text-xs text-[#292522]/50">{formatDate(a.published_at)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* VIENI ANCHE TU */}
      <section className="container-vv py-8 lg:py-16">
        <div className="rounded-[2rem] bg-[#292522] text-[#FFFCF8] p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 blob bg-[#FF6100]/30 pointer-events-none" />
          <div className="relative">
            <div className="eyebrow text-[#FF6100]">Vieni anche tu</div>
            <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl max-w-2xl">Un piccolo gesto, un grande cambiamento.</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Link to="/eventi" className="rounded-2xl bg-[#FFFCF8]/10 hover:bg-[#FFFCF8]/15 p-6 group transition-colors" data-testid="cta-partecipa-evento">
                <Users className="h-6 w-6 text-[#FF6100]" strokeWidth={1.5} />
                <h3 className="mt-3 font-display text-xl font-medium">Vieni a un evento</h3>
                <p className="mt-1 text-sm text-[#FFFCF8]/70">Trova l'incontro giusto per te.</p>
              </Link>
              <Link to="/partecipa#socio" className="rounded-2xl bg-[#FFFCF8]/10 hover:bg-[#FFFCF8]/15 p-6 group transition-colors" data-testid="cta-diventa-socio">
                <HeartHandshake className="h-6 w-6 text-[#FF6100]" strokeWidth={1.5} />
                <h3 className="mt-3 font-display text-xl font-medium">Diventa socio/a</h3>
                <p className="mt-1 text-sm text-[#FFFCF8]/70">Entra a far parte della comunità.</p>
              </Link>
              <Link to="/partecipa#volontariato" className="rounded-2xl bg-[#FFFCF8]/10 hover:bg-[#FFFCF8]/15 p-6 group transition-colors" data-testid="cta-volontariato">
                <Sparkles className="h-6 w-6 text-[#FF6100]" strokeWidth={1.5} />
                <h3 className="mt-3 font-display text-xl font-medium">Dacci una mano</h3>
                <p className="mt-1 text-sm text-[#FFFCF8]/70">Tempo, competenze o piccoli gesti.</p>
              </Link>
              <Link to="/partecipa#sostieni" className="rounded-2xl bg-[#FFFCF8]/10 hover:bg-[#FFFCF8]/15 p-6 group transition-colors" data-testid="cta-sostieni">
                <HeartHandshake className="h-6 w-6 text-[#FF6100]" strokeWidth={1.5} />
                <h3 className="mt-3 font-display text-xl font-medium">Sostieni Vola Vola</h3>
                <p className="mt-1 text-sm text-[#FFFCF8]/70">Bonifico, 5×1000, PayPal o Satispay.</p>
              </Link>
            </div>
            <div className="mt-6">
              <Link to="/partecipa#collabora" className="inline-flex items-center gap-1.5 text-sm text-[#FFFCF8]/80 hover:text-[#FF6100] transition-colors" data-testid="cta-collabora-secondary">
                Sei un ente, una scuola, un/a professionista? <span className="text-[#FF6100] font-semibold">Proponi una collaborazione →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RETI E COLLABORAZIONI */}
      <section className="container-vv py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="eyebrow">Reti e collaborazioni</div>
          <h2 className="mt-3 font-display font-medium text-4xl sm:text-5xl text-[#292522]">Non camminiamo da sole</h2>
          <p className="mt-4 text-lg text-[#292522]/75 leading-relaxed">
            Crediamo che una comunità si costruisca mettendo in relazione persone, competenze e realtà diverse. Per questo lavoriamo insieme ad associazioni, scuole, servizi, istituzioni e realtà del territorio, costruendo collaborazioni che cambiano e crescono insieme ai progetti.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {partners.map((p) => (
            <div key={p.id} className="rounded-2xl bg-white border border-[#292522]/8 px-5 py-6 text-center text-sm font-medium text-[#292522]/75 flex items-center justify-center min-h-[80px]" data-testid={`partner-${p.id}`}>
              {p.name}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
